import * as React from "react";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { AnalyticsConfig } from "uitk-react-utils";
import { UitkBreadcrumbs } from "uitk-react-breadcrumbs";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkSpacing } from "uitk-react-spacing";
import { CrossLOBMenu } from "components/flexComponents/Breadcrumbs/components/CrossLOBMenu";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { withStores } from "stores";
import { BreadcrumbsFlexModuleResult } from "typings/microserviceModels/breadcrumbs-flex-module";
import { TravelAdvisory } from "../TravelAdvisory/TravelAdvisory";
import { SchemaOrgScript } from "src/components/utility/SchemaOrg/SchemaOrgScript";
import { observer } from "mobx-react";
import { BreadcrumbsProps } from "./typings";

export const Breadcrumbs = withStores(
  "analytics",
  "flexModuleModelStore",
  "context"
)(
  observer((props: BreadcrumbsProps) => {
    const { context, flexTemplateNode, flexModuleModelStore, analytics } = props;

    const [isBreadcrumbsMenuOpen, setBreadcrumbsMenuOpen] = React.useState(false);

    if (!flexModuleModelStore || !analytics) {
      return null;
    }

    const { id, options } = flexTemplateNode;
    const { fmId, fmTitleId, hideTravelAdvisory } = options;
    const model = flexModuleModelStore.getModel(id) as BreadcrumbsFlexModuleResult;

    if (!model || model.empty || !model.breadcrumbs) {
      return null;
    }

    const breadcrumbs = model.breadcrumbs;
    const onBreadcrumbsMenuTriggerClick = () => {
      setBreadcrumbsMenuOpen(!isBreadcrumbsMenuOpen);
    };

    // add analytics to the breadcrumbs for tracking
    const breadcrumbsWithAnalytics = breadcrumbs.map((crumb) => {
      const analyticsConfig: AnalyticsConfig = {
        id: "breadcrumbs",
        callback: (moduleName, description) => {
          const flexTrackingInfo: FlexTrackingInfo = {
            moduleName,
            action: Action.CLICK,
            linkName: description,
          };
          sendDelayedTrackEvent(flexTrackingInfo, analytics);
        },
      };

      return {
        text: crumb.text,
        url: crumb.url,
        analytics: analyticsConfig,
      };
    });

    // create Strucured Data for breadcrumbs
    const breadcrumbStructuredData = (): string => {
      const itemListElement = breadcrumbs.map((crumb) => {
        const listItem = {
          "@type": "ListItem",
          name: crumb.text,
          position: crumb.index,
        };

        if (crumb.fullUrl) {
          listItem["item"] = crumb.fullUrl;
        }

        return listItem;
      });

      const structuredDataObject = {
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        itemListElement,
      };

      return JSON.stringify(structuredDataObject);
    };

    const menuAnalytics: AnalyticsConfig = {
      id: "breadcrumbs.menu",
      callback: (moduleName, description) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName,
          action: Action.CLICK,
          linkName: description,
        };
        sendDelayedTrackEvent(flexTrackingInfo, analytics);
      },
    };

    // find the first breadcrumbs with the dropdown, will be used for the dropdown Plan your trip menu
    const breadcrumbsDropdown = breadcrumbs.find((crumb) => !!crumb.dropDown);

    return (
      <>
        <SchemaOrgScript structuredData={breadcrumbStructuredData()} />
        <div className="Breadcrumbs" id={id} data-fm={fmId} data-fm-title-id={fmTitleId}>
          <UitkLayoutFlex justifyContent="space-between" alignItems="center" className="breadcrumbs-alignment">
            <UitkLayoutFlexItem>
              <Viewport>
                <ViewSmall>
                  <UitkSpacing padding={{ blockstart: "one" }}>
                    <UitkBreadcrumbs
                      crumbs={breadcrumbsWithAnalytics}
                      crumbsToDisplay={1}
                      isOpen={isBreadcrumbsMenuOpen}
                      menuAnalytics={menuAnalytics}
                      onTriggerClick={onBreadcrumbsMenuTriggerClick}
                    />
                  </UitkSpacing>
                </ViewSmall>
                <ViewMedium>
                  <UitkSpacing padding={{ block: "two" }}>
                    <UitkBreadcrumbs crumbs={breadcrumbsWithAnalytics} />
                  </UitkSpacing>
                </ViewMedium>
              </Viewport>
            </UitkLayoutFlexItem>
            {breadcrumbsDropdown && (
              <UitkLayoutFlexItem>
                <CrossLOBMenu items={breadcrumbsDropdown.dropDown} analyticsStore={analytics} />
              </UitkLayoutFlexItem>
            )}
          </UitkLayoutFlex>
        </div>
        {/* By default, we show TravelAdvisory if LP_SC_Covid19_TravelAdvisory_for_LandingPages is enabled */}
        {!hideTravelAdvisory && (
          <UitkLayoutFlex data-testid="travelAdvisory" className="TravelAdvisory">
            <UitkLayoutFlexItem>
              <TravelAdvisory context={context} />
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        )}
      </>
    );
  })
);

export default Breadcrumbs;
